import React, { useEffect, useState, useCallback } from 'react';
import ReactConfetti from 'react-confetti';
import { Box, Typography, Slide, Paper } from '@mui/material';
import { 
  EmojiEvents as TrophyIcon,
  TrendingDown as CostDownIcon,
  LocalShipping as DeliveryIcon,
  Nature as EcoIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';

export type AchievementType = 
  | 'streak_7_days'
  | 'cost_reduction'
  | 'delivery_improved'
  | 'carbon_reduced'
  | 'resilience_improved'
  | 'first_simulation'
  | 'supplier_added'
  | 'custom';

interface Achievement {
  type: AchievementType;
  title: string;
  message: string;
  icon: React.ReactNode;
  color: string;
  sound?: string;
}

const achievementConfig: Record<AchievementType, Omit<Achievement, 'type'>> = {
  streak_7_days: {
    title: '🔥 7-Day Streak!',
    message: "You've logged in 7 days in a row! Your supply chain loves the attention.",
    icon: <TrophyIcon sx={{ fontSize: 48 }} />,
    color: '#F59E0B',
  },
  cost_reduction: {
    title: '💰 Cost Cutter!',
    message: 'Your carrying costs dropped! More money in your pocket.',
    icon: <CostDownIcon sx={{ fontSize: 48 }} />,
    color: '#10B981',
    sound: 'cash-register',
  },
  delivery_improved: {
    title: '🚀 Speed Demon!',
    message: 'Your lead times improved! Customers will love the faster delivery.',
    icon: <DeliveryIcon sx={{ fontSize: 48 }} />,
    color: '#3B82F6',
  },
  carbon_reduced: {
    title: '🌱 Eco Warrior!',
    message: 'Carbon footprint reduced! The planet thanks you.',
    icon: <EcoIcon sx={{ fontSize: 48 }} />,
    color: '#22C55E',
  },
  resilience_improved: {
    title: '🛡️ Fortress Built!',
    message: 'Supply chain resilience increased! Ready for anything.',
    icon: <SpeedIcon sx={{ fontSize: 48 }} />,
    color: '#8B5CF6',
  },
  first_simulation: {
    title: '🎮 First Simulation!',
    message: 'You ran your first what-if scenario! Knowledge is power.',
    icon: <TrophyIcon sx={{ fontSize: 48 }} />,
    color: '#EC4899',
  },
  supplier_added: {
    title: '🤝 Network Expanded!',
    message: 'New supplier added to your network! More options = more power.',
    icon: <TrophyIcon sx={{ fontSize: 48 }} />,
    color: '#06B6D4',
  },
  custom: {
    title: '🎉 Achievement Unlocked!',
    message: 'Great job!',
    icon: <TrophyIcon sx={{ fontSize: 48 }} />,
    color: '#F59E0B',
  },
};

interface ConfettiCelebrationProps {
  achievement: AchievementType | null;
  customTitle?: string;
  customMessage?: string;
  onComplete?: () => void;
  duration?: number;
}

/**
 * Confetti Celebration - Triggers dopamine for "boring wins"
 * 
 * Shows full-screen confetti and achievement notification when
 * users hit micro-milestones that lead to business success.
 */
const ConfettiCelebration: React.FC<ConfettiCelebrationProps> = ({
  achievement,
  customTitle,
  customMessage,
  onComplete,
  duration = 5000,
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  const handleResize = useCallback(() => {
    setWindowSize({ width: window.innerWidth, height: window.innerHeight });
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (achievement) {
      setShowConfetti(true);
      setShowMessage(true);

      // Play sound effect if available
      const config = achievementConfig[achievement];
      if (config.sound === 'cash-register') {
        // Could integrate with Web Audio API for sound effects
        // For now, just visual celebration
      }

      // Hide confetti after duration
      const confettiTimer = setTimeout(() => {
        setShowConfetti(false);
      }, duration - 1000);

      // Hide message and trigger complete
      const messageTimer = setTimeout(() => {
        setShowMessage(false);
        onComplete?.();
      }, duration);

      return () => {
        clearTimeout(confettiTimer);
        clearTimeout(messageTimer);
      };
    }
  }, [achievement, duration, onComplete]);

  if (!achievement) return null;

  const config = achievementConfig[achievement];
  const title = customTitle || config.title;
  const message = customMessage || config.message;

  return (
    <>
      {/* Full-screen confetti */}
      {showConfetti && (
        <ReactConfetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.3}
          colors={['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#22C55E']}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }}
        />
      )}

      {/* Achievement notification */}
      <Slide direction="down" in={showMessage} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            p: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${config.color}22 0%, ${config.color}11 100%)`,
            border: `2px solid ${config.color}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            maxWidth: 500,
            animation: 'pulse 0.5s ease-in-out infinite alternate',
            '@keyframes pulse': {
              '0%': { transform: 'translateX(-50%) scale(1)' },
              '100%': { transform: 'translateX(-50%) scale(1.02)' },
            },
          }}
        >
          <Box sx={{ color: config.color }}>
            {config.icon}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: config.color }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {message}
            </Typography>
          </Box>
        </Paper>
      </Slide>
    </>
  );
};

// Hook to trigger celebrations
export const useConfetti = () => {
  const [currentAchievement, setCurrentAchievement] = useState<AchievementType | null>(null);

  const celebrate = (achievement: AchievementType) => {
    setCurrentAchievement(achievement);
  };

  const clearCelebration = () => {
    setCurrentAchievement(null);
  };

  return {
    currentAchievement,
    celebrate,
    clearCelebration,
    ConfettiComponent: (
      <ConfettiCelebration
        achievement={currentAchievement}
        onComplete={clearCelebration}
      />
    ),
  };
};

export default ConfettiCelebration;
